import { joinURL } from 'ufo'
import { useErrorLogger } from '~/composables/useErrorLogger'
import { DEFAULT_GUMLET_PUBLIC_HOST } from '~/constants/defaultGumletPublicHost'

/**
 * Output WebP + Gumlet’s lossy `compress` (no w/h/fit — layout stays in CSS).
 * @see https://docs.gumlet.com/reference/image-formats
 */
const GUMLET_OUTPUT = 'format=webp&compress=true'

/**
 * S3 **object** REST hostnames only (one regexp). Rejects bare `*.amazonaws.com` and hosts without
 * strict `s3` / `\\.s3\\.` shapes (e.g. `ec2…`, `*.s3-proxy…`, `s3-control…`).
 * Unlisted AWS endpoint forms ⇒ false negative (no Gumlet rewrite).
 */
const AWS_S3_OBJECT_HOSTNAME_RE =
  /^(?:s3\.amazonaws\.com(?:\.cn)?|s3\.dualstack\.[a-z0-9-]+\.amazonaws\.com(?:\.cn)?|s3\.[a-z0-9-]+\.amazonaws\.com(?:\.cn)?|s3-cn-[a-z0-9-]+-\d+\.amazonaws\.com\.cn|s3-(?:us|eu|ap|ca|sa|me|af|cn|il)-[a-z0-9-]+-\d+\.amazonaws\.com(?:\.cn)?|.+?\.s3-accelerate(?:\.dualstack)?\.amazonaws\.com|.+?\.s3\.amazonaws\.com(?:\.cn)?|.+?\.s3\.dualstack\.[a-z0-9-]+\.amazonaws\.com(?:\.cn)?|.+?\.s3\.[a-z0-9-]+\.amazonaws\.com(?:\.cn)?|.+?\.s3-(?:us|eu|ap|ca|sa|me|af|cn|il)-[a-z0-9-]+-\d+\.amazonaws\.com(?:\.cn)?|.+?\.s3-cn-[a-z0-9-]+-\d+\.amazonaws\.com\.cn)$/i

function isAwsS3ObjectHostname (hostname: string): boolean {
  return AWS_S3_OBJECT_HOSTNAME_RE.test(hostname.toLowerCase())
}

/** Path-style URLs use an `s3…amazonaws…` hostname (bucket in the first pathname segment). */
function isPathStyleS3Host (hostname: string): boolean {
  const h = hostname.toLowerCase()
  return isAwsS3ObjectHostname(h) && /^s3(?:[.-]|$)/.test(h)
}

function isAwsS3ObjectHttpUrl (parsed: URL): boolean {
  return isAwsS3ObjectHostname(parsed.hostname)
}

/**
 * Pulse static CDN hosts (`PULSE_ASSESTS`, ZigZag bundles). Never Gumlet — must stay exact origin
 * even when `src` is shaped oddly or env gates change.
 */
function isPulseResourcesCdnHost (hostname: string): boolean {
  const lower = hostname.toLowerCase()
  if (!lower.endsWith('.idtech.com')) { return false }
  const h = lower.startsWith('www.') ? lower.slice(4) : lower
  return h.startsWith('pulse-res-')
}

const BUCKET_NAME_FIRST_SEGMENT = new Set([
  'idtech-2018-media-prd',
  'idtech-2018-media-stg',
  'idtech-2018-media.dev',
  'idtech.2018.media.dev',
  'idtech-2018-media-dev'
])

/**
 * Ensures a URL pathname is absolute (starts with `/`) so segment splitting is consistent.
 */
function normalizePathname (pathname: string): string {
  return pathname.startsWith('/') ? pathname : `/${pathname}`
}

/**
 * Pulse `common-assets/` tree (NavBar logo, shared Pulse bundles). Never map onto Gumlet —
 * even when `src` is mis-shaped (e.g. bucket-prefixed relative paths that strip to `/common-assets/...`).
 */
function isCommonAssetsMediaPath (pathnameOrKey: string): boolean {
  return normalizePathname(pathnameOrKey).toLowerCase().startsWith('/common-assets/')
}

/**
 * True when `src` should be rewritten onto `gumletHost` (S3-backed media).
 * Pulse CDN hosts (`pulse-res-*.idtech.com`), paths already on `gumletHost`, root-relative
 * `/common-assets/*`, and other non-S3 URLs are left unchanged.
 */
function shouldRewriteSrcThroughGumlet (
  src: string,
  gumletHost: string
): boolean {
  if (src.startsWith('http://') || src.startsWith('https://')) {
    try {
      const parsed = new URL(src)
      if (parsed.hostname === gumletHost) {
        return false
      }
      const absolutePath = normalizePathname(parsed.pathname)
      if (isCommonAssetsMediaPath(absolutePath)) {
        return false
      }
      if (isPulseResourcesCdnHost(parsed.hostname)) {
        return false
      }
      if (!isAwsS3ObjectHttpUrl(parsed)) {
        return false
      }
      const strippedKey = removeS3BucketPathPrefix(parsed.pathname, parsed.hostname)
      return !isCommonAssetsMediaPath(strippedKey)
    } catch {
      return false
    }
  }
  const normalized = normalizePathname(src.startsWith('/') ? src : `/${src}`)
  if (isCommonAssetsMediaPath(normalized)) {
    return false
  }
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length === 0) {
    return false
  }
  if (!BUCKET_NAME_FIRST_SEGMENT.has(segments[0])) {
    return false
  }
  const strippedKey = removeS3BucketPathPrefix(normalized)
  return !isCommonAssetsMediaPath(strippedKey)
}

/**
 * Strips the **S3 bucket** from the pathname so Gumlet receives the same object key it expects
 * (path under the Gumlet source), e.g. `/home/hero.jpg` not `/idtech-2018-media-prd/home/hero.jpg`.
 *
 * **Path-style S3** — bucket is the first segment; `hostname` is something like `s3.us-west-1.amazonaws.com`:
 * - URL: `https://s3.us-west-1.amazonaws.com/idtech-2018-media-prd/home_hero.jpg`
 * - `hostname`: `s3.us-west-1.amazonaws.com` → treated as path-style S3
 * - `pathname`: `/idtech-2018-media-prd/home_hero.jpg` → **output** `/home_hero.jpg`
 *
 * **Virtual-hosted S3** — bucket is in the hostname; path is usually already the object key:
 * - URL: `https://idtech-2018-media-prd.s3.us-west-1.amazonaws.com/home_hero.jpg`
 * - `pathname`: `/home_hero.jpg` → often **no strip** (first segment is not the bucket name)
 *
 * **Root-relative / CMS** — first path segment is a known bucket name (see `BUCKET_NAME_FIRST_SEGMENT`):
 * - `pathname`: `/idtech-2018-media-prd/some/asset.png` → **output** `/some/asset.png`
 *
 * If nothing matches, the pathname is only passed through `normalizePathname`.
 */
function removeS3BucketPathPrefix (pathname: string, hostname?: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) { return '/' }
  const [firstSegment, ...remainingSegments] = segments
  const shouldStripBucketPrefix =
    (hostname != null && isPathStyleS3Host(hostname)) ||
    BUCKET_NAME_FIRST_SEGMENT.has(firstSegment)
  if (shouldStripBucketPrefix) {
    return remainingSegments.length > 0 ? `/${remainingSegments.join('/')}` : '/'
  }
  return normalizePathname(pathname)
}

function toGumletPath (inputUrl?: string | null): string | null {
  if (!inputUrl) { return null }
  try {
    const parsed = new URL(inputUrl)
    return removeS3BucketPathPrefix(parsed.pathname, parsed.hostname)
  } catch {
    // `new URL()` throws for relative / partial strings — expected; fall back below.
    if (inputUrl.startsWith('/')) {
      return removeS3BucketPathPrefix(normalizePathname(inputUrl))
    }
  }
  return removeS3BucketPathPrefix(normalizePathname(String(inputUrl)))
}

type PublicGumlet = { gumletEnabled?: boolean; gumletHost?: string; gumletImageErrorFallback?: boolean }

/**
 * Gumlet rewrites are limited to the marketing homepage and summer-camps tree when the global flag is on.
 * On **SSR**, the allowlist uses `useRequestURL().pathname` (incoming request); on the **client**, `useRoute().path`, so gating matches the rendered page and avoids router-timing hydration skew vs `useRoute()`-only on the server.
 */
export function isGumletAllowedForRoutePath (path: string): boolean {
  const p = path === '' ? '/' : path
  if (p === '/') return true
  const normalized = p.replace(/\/+$/, '') || '/'
  if (normalized === '/') return true
  return normalized === '/summer-camps' || normalized.startsWith('/summer-camps/')
}

function isGumletRouteAllowed (): boolean {
  try {
    if (import.meta.server) {
      try {
        const path = useRequestURL().pathname || '/'
        return isGumletAllowedForRoutePath(path)
      } catch {
        /** Request URL unavailable (rare); fall back to router. */
      }
    }
    const route = useRoute()
    return isGumletAllowedForRoutePath(route.path)
  } catch {
    return false
  }
}

function gumletHostname (config: { public: PublicGumlet }): string {
  const raw = config.public.gumletHost || DEFAULT_GUMLET_PUBLIC_HOST
  return raw.replace(/^https?:\/\//i, '').replace(/\/$/, '').toLowerCase()
}

function gumletHttpsOrigin (config: { public: PublicGumlet }): string {
  return `https://${gumletHostname(config)}`
}

/**
 * Gumlet URL → original `src` for `<img>` load fallback. Keys use **origin + pathname + sorted query**
 * so different hosts, paths, or transform params do not collide (pathname-only keys were unsafe).
 * Bounded by {@link MAX_GUMLET_IMAGE_FALLBACK_ENTRIES} (FIFO eviction); entries are also removed after a successful swap.
 */
const gumletImageUrlToOriginalSrc = new Map<string, string>()

/** Max distinct Gumlet image URLs tracked per tab (~long editorial pages / SPA navigations). Oldest insert evicted when full. */
const MAX_GUMLET_IMAGE_FALLBACK_ENTRIES = 512

/** Canonical map key: origin (scheme + host + port), pathname, and query sorted by param name/value. */
function stableGumletImageCacheKey (absoluteOrRelativeUrl: string, baseHref: string): string {
  const u = new URL(absoluteOrRelativeUrl, baseHref)
  const entries = [...u.searchParams.entries()].sort(([aK, aV], [bK, bV]) =>
    aK.localeCompare(bK) || String(aV).localeCompare(String(bV)))
  const qs = entries.length ? `?${new URLSearchParams(entries).toString()}` : ''
  return `${u.origin}${u.pathname}${qs}`
}

function evictOldestGumletImageFallbackEntryIfFull (): void {
  while (gumletImageUrlToOriginalSrc.size >= MAX_GUMLET_IMAGE_FALLBACK_ENTRIES) {
    const oldest = gumletImageUrlToOriginalSrc.keys().next().value
    if (oldest === undefined) { return }
    gumletImageUrlToOriginalSrc.delete(oldest)
  }
}

export function registerGumletImageRewriteForFallback (rewrittenUrl: string, originalSrc: string): void {
  if (!import.meta.client || !originalSrc) { return }
  try {
    const base = typeof location !== 'undefined' ? location.href : 'https://localhost/'
    const key = stableGumletImageCacheKey(rewrittenUrl, base)
    if (!gumletImageUrlToOriginalSrc.has(key)) {
      evictOldestGumletImageFallbackEntryIfFull()
    }
    gumletImageUrlToOriginalSrc.set(key, originalSrc)
  } catch {
    // ignore
  }
}

export function resolveOriginalSrcForFailedGumletImage (failedAbsoluteUrl: string): string | null {
  if (!import.meta.client) { return null }
  try {
    const key = stableGumletImageCacheKey(failedAbsoluteUrl, location.href)
    return gumletImageUrlToOriginalSrc.get(key) ?? null
  } catch {
    return null
  }
}

/** Drop mapping after a successful fallback so the map does not retain entries we no longer need. */
export function forgetGumletImageFallbackMapping (gumletAbsoluteUrl: string): void {
  if (!import.meta.client) { return }
  try {
    const key = stableGumletImageCacheKey(gumletAbsoluteUrl, location.href)
    gumletImageUrlToOriginalSrc.delete(key)
  } catch {
    // ignore
  }
}

/**
 * Nuxt Image: when `gumletEnabled === true`, rewrite **S3-backed** media to Gumlet with
 * `?format=webp&compress=true` **only on `/` and `/summer-camps` (and subpaths)**.
 * Pulse CDN hosts, `common-assets/` keys, non-S3 URLs, and URLs already
 * on `gumletHost` are returned unchanged.
 */
export function gumletImageUrlOrOriginal (
  src: string,
  _modifiers: Record<string, unknown>,
  config: { public: PublicGumlet }
): string {
  if (config.public.gumletEnabled !== true) {
    return src
  }
  if (!isGumletRouteAllowed()) {
    return src
  }
  const { logError } = useErrorLogger()
  const host = gumletHostname(config)
  try {
    if (!shouldRewriteSrcThroughGumlet(src, host)) {
      return src
    }
    const path = toGumletPath(src) || src
    if (isCommonAssetsMediaPath(path)) {
      return src
    }
    const out = joinURL(gumletHttpsOrigin(config), `${path}?${GUMLET_OUTPUT}`)
    registerGumletImageRewriteForFallback(out, src)
    return out
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    logError(error, { context: 'gumletImageUrlOrOriginal failed, using original src', src, gumlet: true })
    return src
  }
}

/**
 * Rewrites **S3-backed** video URLs to Gumlet when enabled (no image query params), **only on `/` and
 * `/summer-camps` (and subpaths)**.
 * Same exclusions as images: Pulse CDN, `common-assets/`, non-S3, already on `gumletHost`.
 */
export function gumletVideoUrlOrOriginal (
  src: string,
  config: { public: PublicGumlet }
): string {
  if (config.public.gumletEnabled !== true) {
    return src
  }
  if (!isGumletRouteAllowed()) {
    return src
  }
  const { logError } = useErrorLogger()
  const host = gumletHostname(config)
  try {
    if (!shouldRewriteSrcThroughGumlet(src, host)) {
      return src
    }
    const path = toGumletPath(src) || src
    if (isCommonAssetsMediaPath(path)) {
      return src
    }
    return joinURL(gumletHttpsOrigin(config), path)
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    logError(error, { context: 'gumletVideoUrlOrOriginal failed, using original src', src, gumlet: true })
    return src
  }
}
